import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import CreditHistoryField from "../shared/CreditHistoryField.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import { buildUploadLabelMap } from "../shared/formValidation.js";
import { IF_FORM_WIZARD_STEPS, IF_UPLOAD_BUCKETS } from "../shared/borrowerUploadConfig.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";

const schema = z.object({
  firstName: z.string().min(1, "First Name is required"),
  surname: z.string().min(1, "Surname is required"),
  phone: z.string().min(1, PHONE_ERROR_MOBILE).refine((v) => isValidPhone(v), { message: PHONE_ERROR_MOBILE }),
  email: z.string().email("Invalid email address"),
  credit: z.enum(["Good", "Bad", "Poor"], {
    errorMap: () => ({ message: "Please select credit history" }),
  }),
});

const DETAILS_FIELDS = ["firstName", "surname", "phone", "email", "credit"];

export default function IfSoloForm({
  onBack,
  onNext,
  initialPayload,
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("if-solo", productId || initialPayload?.productId));
  const initial = savedDraft?.if?.solo || initialPayload?.if?.solo || {};
  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial.uploads || {});

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, touchedFields },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      firstName: initial.firstName || "",
      surname: initial.surname || "",
      phone: initial.phone || "",
      email: initial.email || "",
      credit: initial.credit || "",
    },
  });

  const currentValues = watch();
  const draftKey = getDraftStorageKey("if-solo", productId || initialPayload?.productId);

  const constructPayload = (formValues, currentUploads, extras = {}) => {
    const updatedFormData = {
      ...(initialPayload?.formData || {}),
      firstName: formValues.firstName,
      surname: formValues.surname,
      phone: formValues.phone,
      email: formValues.email,
      credit: formValues.credit,
      first_name: formValues.firstName,
      last_name: formValues.surname,
      phone_number: formValues.phone,
      credit_history: formValues.credit,
      ...extras,
    };
    return {
      ...(initialPayload || {}),
      formData: updatedFormData,
      if: {
        ...(initialPayload?.if || {}),
        borrowerType: "Solo",
        solo: { ...formValues, uploads: currentUploads },
      },
    };
  };

  const draftSnapshot = useMemo(
    () => constructPayload(currentValues, uploads),
    [currentValues, uploads, initialPayload]
  );

  const stepErrors = useMemo(
    () => ({
      details: DETAILS_FIELDS.some((f) => errors[f]),
      documents: false,
      review: false,
    }),
    [errors]
  );

  const validateStep = async (stepIndex, stepId) => {
    if (stepId === "details" || stepIndex === 0) return trigger(DETAILS_FIELDS);
    return true;
  };

  const finish = (extras = {}) => {
    const payload = constructPayload(currentValues, uploads, extras);
    onNext?.({
      type: "solo",
      firstName: currentValues.firstName,
      surname: currentValues.surname,
      phone: currentValues.phone,
      email: currentValues.email,
      credit: currentValues.credit,
      uploads,
      payloadObj: payload,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(IF_UPLOAD_BUCKETS), []);

  return (
    <>
      <BorrowerSteppedForm
        title="INVOICE FINANCE — SOLE TRADER"
        subtitle="Please complete all required fields and upload necessary documents"
        steps={IF_FORM_WIZARD_STEPS}
        draftKey={draftKey}
        draftSnapshot={draftSnapshot}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        isSubmitting={isSubmitting}
        detailsContinueDisabled={!isValidPhone(currentValues.phone)}
        renderDetails={() => (
          <div>
            <SectionTitle>Personal Information</SectionTitle>
            <FormFieldRow>
              <FormField id="if-solo-firstName" label="First Name" required error={errors.firstName?.message} touched={touchedFields.firstName}>
                <input id="if-solo-firstName" className={`input w-full ${errors.firstName ? "border-rose-300" : ""}`} {...register("firstName")} />
              </FormField>
              <FormField id="if-solo-surname" label="Surname" required error={errors.surname?.message} touched={touchedFields.surname}>
                <input id="if-solo-surname" className={`input w-full ${errors.surname ? "border-rose-300" : ""}`} {...register("surname")} />
              </FormField>
              <FormField id="if-solo-phone" label="Phone Number" required>
                <PhoneField
                  id="if-solo-phone"
                  value={watch("phone")}
                  onChange={(next) => setValue("phone", next, { shouldValidate: true, shouldTouch: true })}
                  onBlur={() => trigger("phone")}
                />
              </FormField>
              <FormField id="if-solo-email" label="Email" required error={errors.email?.message} touched={touchedFields.email}>
                <input id="if-solo-email" type="email" className={`input w-full ${errors.email ? "border-rose-300" : ""}`} {...register("email")} />
              </FormField>
            </FormFieldRow>
            <div className="mt-4">
              <CreditHistoryField
                id="if-solo-credit"
                value={currentValues.credit}
                error={errors.credit?.message}
                touched={touchedFields.credit}
                register={register("credit")}
              />
            </div>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel
            buckets={IF_UPLOAD_BUCKETS}
            uploads={uploads}
            uploadingFiles={uploadingFiles}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            documentsStepIndex={1}
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "First Name", value: currentValues.firstName },
                  { label: "Surname", value: currentValues.surname },
                  { label: "Phone", value: currentValues.phone },
                  { label: "Email", value: currentValues.email },
                  { label: "Credit History", value: currentValues.credit },
                ],
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
